import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import Card from ".";
import colors from "../../config/colors";
import { useDimensions, usePayment } from "../../hooks";
import { formatCurrency, getChargeRows } from "../../utils";
import { TRANSACTION_STATUS } from "../../constants";

const SuccessCard = ({
  totalAmount,
  text,
  instrumentFee = "",
  registrationFee = "",
  totalFees = "",
  paymentReference,
  currency,
}: {
  totalAmount: string;
  text?: string;
  instrumentFee?: string;
  registrationFee?: string;
  totalFees?: string;
  // The reference the payer quotes to the school or to support. Absent
  // whenever the payment has none that qualifies, and absent means nothing
  // renders -- no label, no empty row.
  paymentReference?: string;
  // The unit the pay response named, for the amounts on the receipt.
  currency?: string;
}) => {
  // The hook, matching errorCard: the card subscribes to locale changes
  // itself instead of leaning on a parent that happens to re-render.
  const { t } = useTranslation();
  const { isSmall } = useDimensions();
  const { success, pending } = TRANSACTION_STATUS;
  const maxWidth = isSmall ? "unset" : "38%";

  // Same breakdown the payer was shown before paying, from one owner, so
  // the receipt cannot contradict the confirm screen. Absent on a reload,
  // where the session no longer holds the quote.
  const chargeRows = Number(totalFees) > 0
    ? getChargeRows(instrumentFee, registrationFee, totalFees)
    : [];

  return (
    <Box sx={{ maxWidth }}>
      <Card
        sx={{
          display: "flex",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          padding: isSmall ? 3 : 10,
          paddingY: 2,
        }}
      >
        <Box>
          <Box display={"flex"} justifyContent={"center"}>
            <CheckCircleIcon color="success" sx={{ fontSize: 70 }} />
          </Box>

          <Typography
            fontSize={32}
            fontWeight={600}
            textAlign={"center"}
            color={"#057A18"}
            paddingBottom={5}
          >
            {t("stepper_3.successful_payment")}{" "}
          </Typography>
          <Typography
            textAlign={"center"}
            sx={{ fontWeight: "400", fontSize: 16, color: "#707070" }}
            paddingBottom={3}
          >
            {t("stepper_3.payment_success_message")}
          </Typography>
          {Number(totalAmount) > 0 && (
            <Typography
              textAlign={"center"}
              fontSize={24}
              fontWeight={700}
              paddingBottom={2}
              sx={{ marginTop: -3 }}
            >
              {formatCurrency(totalAmount, undefined, currency)}
            </Typography>
          )}
          {chargeRows.length > 0 && (
            <Box paddingBottom={2} sx={{ marginTop: -1 }}>
              {chargeRows.map(row => (
                <Box
                  key={row.titleKey}
                  display={"flex"}
                  justifyContent={"space-between"}
                >
                  <Typography sx={{ fontSize: 14, color: "#707070" }}>
                    {t(row.titleKey)}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#707070" }}>
                    {formatCurrency(row.value, undefined, currency)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {paymentReference && (
            <Box paddingBottom={2}>
              <Typography
                textAlign={"center"}
                sx={{ fontWeight: "400", fontSize: 14, color: colors.mutedTextColor }}
              >
                {t("payment_reference.label")}
              </Typography>
              <Typography
                textAlign={"center"}
                sx={{
                  fontWeight: "600",
                  fontSize: 14,
                  color: colors.mutedTextColor,
                  // No spaces to break on, and the card is 38% of the viewport
                  // on desktop.
                  wordBreak: "break-all",
                  userSelect: "text",
                }}
              >
                {paymentReference}
              </Typography>
              <Typography
                textAlign={"center"}
                sx={{ fontWeight: "400", fontSize: 14, color: colors.mutedTextColor }}
                paddingTop={1}
              >
                {t("payment_reference.help")}
              </Typography>
            </Box>
          )}
          {text === pending && (
            <>
              <Box
                height={5}
                width={"100%"}
                sx={{ backgroundColor: "gray", marginBottom: 5 }}
              >
                <Box
                  height={5}
                  width={"50%"}
                  sx={{ backgroundColor: "green", marginBottom: 5 }}
                />
              </Box>
              <Box display={"flex"} justifyContent={"center"}>
                <CircularProgress color="primary" title="loading" />
              </Box>
              <Typography
                style={{ fontSize: 24, fontWeight: "500", textAlign: "center" }}
              >
                {t("stepper_3.processing_bill")}
              </Typography>
            </>
          )}

          {text === success && (
            <>
              <Box
                height={5}
                width={"100%"}
                sx={{ backgroundColor: "gray", marginBottom: 2 }}
              >
                <Box
                  height={5}
                  width={"100%"}
                  sx={{ backgroundColor: "green", marginBottom: 2 }}
                />
              </Box>
              <Typography
                style={{ fontSize: 24, fontWeight: "500", textAlign: "center" }}
              >
                {t("stepper_3.bill_paid")}
              </Typography>
            </>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default SuccessCard;
